/**
 * Infinite pagination for collection, search, blog and other paginated grids.
 * Collection and product-search results are automatically loaded as the customer
 * approaches the end of the current product grid. Other templates retain the theme's
 * configured pagination behavior.
 */
if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();

      this.fetchInProgress = false;
      this.autoLoadTimer = null;
      this.reinitTimer = null;
      this.clickHandler = this.onNextButtonClick.bind(this);
      this.handleContentMutations = this.onContentMutations.bind(this);

      this.contentObserver = new MutationObserver(this.handleContentMutations);
      this.contentObserver.observe(this, {childList: true, subtree: true});

      this.init();
    }

    disconnectedCallback() {
      this.destroy();
      this.contentObserver?.disconnect();
      clearTimeout(this.reinitTimer);
    }

    init() {
      this.destroyBindings();

      this.paginationContainer = this.querySelector('.js-pagination');
      this.paginationItems = this.querySelectorAll('.js-pagination__item');
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.statusElement = this.querySelector('.vsm-infinite-scroll__status');

      if (
        !this.paginationContainer
        || !this.paginationItems.length
        || !this.nextButton
        || !this.nextButton.hasAttribute('href')
      ) {
        return;
      }

      this.autoLoadEnabled =
        this.dataset.infiniteScrollEnabled === 'true'
        || this.paginationContainer.classList.contains('pagination-wrapper--auto-products');

      this.bindListeners();
    }

    destroyBindings() {
      clearTimeout(this.autoLoadTimer);
      this.autoLoadTimer = null;

      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = null;
    }

    destroy() {
      this.destroyBindings();
      clearTimeout(this.reinitTimer);
      this.reinitTimer = null;
    }

    onContentMutations(mutations) {
      if (this.fetchInProgress) return;

      const paginationChanged = mutations.some((mutation) => {
        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

        return changedNodes.some((node) => {
          if (!(node instanceof Element)) return false;

          return node.matches?.('.js-pagination, #ProductGridContainer')
            || Boolean(node.querySelector?.('.js-pagination, #ProductGridContainer'));
        });
      });

      if (!paginationChanged) return;

      clearTimeout(this.reinitTimer);
      this.reinitTimer = setTimeout(() => this.init(), 60);
    }

    onNextButtonClick(event) {
      event.preventDefault();
      this.fetchNextItems();
    }

    bindListeners() {
      this.nextButton.addEventListener('click', this.clickHandler);

      if (!this.autoLoadEnabled) return;

      this.intersectionObserver = new IntersectionObserver((entries) => {
        const entry = entries.find((item) => item.target === this.paginationContainer);
        if (!entry?.isIntersecting) return;

        this.fetchNextItems();
      }, {
        rootMargin: `${Math.min(window.innerHeight, 1000)}px 0px`,
        threshold: 0
      });

      this.intersectionObserver.observe(this.paginationContainer);
    }

    setLoading(isLoading) {
      if (!this.paginationContainer) return;

      this.paginationContainer.classList.toggle('is-loading', isLoading);
      this.paginationContainer.classList.remove('has-error');
      this.paginationContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      if (this.nextButton) {
        if (isLoading) {
          this.nextButton.setAttribute('aria-disabled', 'true');
        } else {
          this.nextButton.removeAttribute('aria-disabled');
        }
      }
    }

    isPaginationNearViewport() {
      if (!this.paginationContainer?.isConnected) return false;

      const rect = this.paginationContainer.getBoundingClientRect();
      return rect.top <= window.innerHeight * 1.75 && rect.bottom >= -window.innerHeight;
    }

    scheduleContinuation() {
      if (!this.autoLoadEnabled || !this.nextButton?.hasAttribute('href')) return;

      clearTimeout(this.autoLoadTimer);
      this.autoLoadTimer = setTimeout(() => {
        if (this.isPaginationNearViewport()) {
          this.fetchNextItems();
        }
      }, 260);
    }

    async fetchNextItems() {
      if (
        this.fetchInProgress
        || !this.nextButton
        || !this.nextButton.hasAttribute('href')
      ) {
        return;
      }

      const requestUrl = this.nextButton.href;
      const originalButtonText = this.nextButton.textContent;
      this.fetchInProgress = true;
      this.setLoading(true);

      if (!this.autoLoadEnabled) {
        this.nextButton.textContent = window.strings?.general?.pagination?.loadingText || 'Loading';
      }

      try {
        const response = await fetch(requestUrl, {
          headers: {'X-Requested-With': 'XMLHttpRequest'}
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch ${requestUrl}: ${response.status} ${response.statusText}`);
        }

        const template = document.createElement('template');
        template.innerHTML = await response.text();

        const newItems = Array.from(template.content.querySelectorAll('.js-pagination__item'));
        if (!newItems.length) {
          this.cleanupAfterLastPage();
          return;
        }

        const currentItems = Array.from(this.querySelectorAll('.js-pagination__item'));
        const insertionPoint = currentItems[currentItems.length - 1];
        const fragment = document.createDocumentFragment();

        newItems.forEach((item) => fragment.appendChild(item.cloneNode(true)));
        insertionPoint.after(fragment);

        const firstNewIndex = currentItems.length;
        this.paginationItems = this.querySelectorAll('.js-pagination__item');

        this.updatePaginationControls(template);

        window.setTimeout(() => {
          if (typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger();
          }

          window.renderSelects?.();
          window.loadDesktopOnlyTemplates?.();
          window.stickersReinit?.();

          document.dispatchEvent(new CustomEvent('vsm:infinite-products-loaded', {
            detail: {
              count: newItems.length,
              url: requestUrl
            }
          }));

          if (
            document.body.classList.contains('tab-active')
            && window.theme?.settings.paginationLoadType === 'show_more'
            && !this.autoLoadEnabled
          ) {
            this.paginationItems[firstNewIndex]?.querySelector('a')?.focus();
          }
        }, 120);
      } catch (error) {
        console.error('Failed to load additional products:', error);

        if (this.paginationContainer) {
          this.paginationContainer.classList.add('has-error');
          this.paginationContainer.classList.remove('is-loading');
          this.paginationContainer.setAttribute('aria-busy', 'false');
        }

        if (this.nextButton) {
          this.nextButton.classList.remove('visually-hidden');
          this.nextButton.removeAttribute('aria-disabled');
          this.nextButton.textContent = 'Try loading more products';
        }
      } finally {
        this.fetchInProgress = false;

        if (this.paginationContainer?.isConnected && !this.paginationContainer.classList.contains('has-error')) {
          this.setLoading(false);

          if (this.nextButton && !this.autoLoadEnabled) {
            this.nextButton.textContent = originalButtonText;
          }

          this.scheduleContinuation();
        }
      }
    }

    updatePaginationControls(template) {
      const nextPagination = template.content.querySelector('.js-pagination');
      const nextLink = nextPagination?.querySelector('.js-pagination__next-button[href]');

      if (nextPagination && nextLink) {
        this.paginationContainer.innerHTML = nextPagination.innerHTML;
        this.refreshNextButton();
      } else {
        this.cleanupAfterLastPage();
      }
    }

    refreshNextButton() {
      this.nextButton?.removeEventListener('click', this.clickHandler);
      this.nextButton = this.querySelector('.js-pagination__next-button');
      this.statusElement = this.querySelector('.vsm-infinite-scroll__status');

      if (this.nextButton) {
        this.nextButton.addEventListener('click', this.clickHandler);
      }
    }

    cleanupAfterLastPage() {
      this.intersectionObserver?.disconnect();
      clearTimeout(this.autoLoadTimer);

      this.paginationContainer?.remove();
      this.paginationContainer = null;
      this.nextButton = null;
      this.statusElement = null;

      document.querySelectorAll('.js-when-paginated-only').forEach((element) => element.remove());
    }
  }

  customElements.define('infinite-scroll', InfiniteScroll);
}